import { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json';
import { SortableTree } from 'dnd-kit-sortable-tree';
import { Loader2, Plus, AlertTriangle, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { decoupledSave, decoupledDelete } from '../../services/api';
import {
  transformMenuItems, buildMenuTreeItems, patchNode, collapseAll, collapseSubtree, normalizeUri,
} from './menuTreeApi';
import useMenuTreeDnd from './useMenuTreeDnd';
import { MenuDndItemComponent } from './MenuTreeRow';
import { MenuTreeCtx } from './menuTreeContext';

/** Find a node by its _item.key in the dnd tree. */
function findNode(items, key) {
  for (const node of items) {
    if (node._item?.key === key) return node;
    if (node.children?.length) {
      const found = findNode(node.children, key);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Front-end administration tree for a Drupal menu (generic decoupled endpoints,
 * no custom module). Drag-drop reorder/nest (no refetch) + inline title/link
 * edition. Create / full edition use the YAML modal (Modal + DataSync) declared
 * in menuAdmin.yaml: this component opens it by writing to the global store, and
 * reloads when the page signals a successful save via ~~.menuRefresh.
 */
export const MenuAdminTree = ({ props = {} }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const menuName = evaluateTemplateValue({ valueToEvaluate: props.menuName, globalDataContext, templateContext }) || 'main';
  // With menu_item_extras the bundle == menu machine name. Fall back to it, and
  // guard against a stale 'menu_link_content' value (the pre-menu_item_extras
  // default bundle no longer exists → would 400).
  let bundle = evaluateTemplateValue({ valueToEvaluate: props.bundle, globalDataContext, templateContext }) || menuName;
  if (bundle === 'menu_link_content') bundle = menuName;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Inline edition of a single row: { key, mode: 'title' | 'link' } | null.
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `/decoupled-api/menu_link_content/${encodeURIComponent(bundle)}/collection`
        + `?display=decoupled&filter[0][f]=menu_name&filter[0][v]=${encodeURIComponent(menuName)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`Chargement du menu échoué (${res.status})`);
      const json = await res.json();
      const rows = Array.isArray(json) ? json : (json.data ?? json.items ?? []);
      const flat = transformMenuItems(rows);
      setItems(buildMenuTreeItems(flat, new Set(flat.map((i) => i.key))));
    } catch (err) {
      setError(err.message || 'Échec du chargement du menu.');
    } finally {
      setLoading(false);
    }
  }, [menuName, bundle]);

  useEffect(() => { reload(); }, [reload]);

  // Reload when the YAML modal signals a successful save (DataSync syncSuccess
  // sets ~~.menuRefresh = true); reset the flag so it only fires once.
  const menuRefresh = evaluateTemplateValue({ valueToEvaluate: '~~.menuRefresh', globalDataContext, templateContext });
  useEffect(() => {
    if (menuRefresh) {
      reload();
      globalDataContext.updateData(false, 'menuRefresh');
    }
  }, [menuRefresh, reload, globalDataContext]);

  const { handleItemsChanged, saving } = useMenuTreeDnd({ items, setItems, bundle });

  // Open the YAML modal (Modal + DataSync) by writing to the global store.
  const openModal = useCallback((cfg) => {
    const params = new URLSearchParams({ display: 'decoupled', entity_type: 'menu_link_content', bundle });
    if (cfg.id != null) params.set('entity_id', cfg.id);
    globalDataContext.updateData(
      { submission_url: `/decoupled-api/save?${params}`, status: null, data: cfg.data },
      'menuItem',
    );
    globalDataContext.updateData(!!cfg.isEdit, 'menuModal.isEdit');
    globalDataContext.updateData(true, 'menuModal.open');
  }, [bundle, globalDataContext]);

  // Optimistic inline update (no refetch) with rollback on failure.
  const patchLocal = useCallback(async (item, patch, savePatch) => {
    const before = items;
    setItems((cur) => patchNode(cur, item.id, patch));
    try {
      await decoupledSave('menu_link_content', bundle, savePatch, item.id);
    } catch (err) {
      console.error('[MenuTree] Save failed:', err.message);
      setItems(before);
    }
  }, [items, bundle]);

  // Inline confirm for the currently edited row (title or link).
  const handleEditConfirm = useCallback((item, value) => {
    const mode = editing?.mode;
    setEditing(null);
    if (item.id == null) return;
    if (mode === 'title') {
      if (value !== item.title) patchLocal(item, { title: value }, { title: value });
    } else {
      const uri = normalizeUri(value);
      if (uri !== item.uri) patchLocal(item, { uri }, { link: { uri, title: '' } });
    }
  }, [editing, patchLocal]);

  const handleCreate = useCallback((pos, item) => {
    let parent = '';
    let weight = 0;
    if (pos === 'child') {
      parent = item.key;
      weight = (findNode(items, item.key)?.children?.length) || 0;
    } else {
      parent = item.parentKey || '';
      weight = (item.weight ?? 0) + (pos === 'after' ? 1 : -1);
    }
    openModal({
      isEdit: false,
      data: { title: '', link: { uri: '' }, enabled: true, description: '', menu_name: menuName, parent, weight },
    });
  }, [items, openModal, menuName]);

  const handleDelete = useCallback(async (item) => {
    if (item.id == null) return;
    if (!window.confirm(`Supprimer le lien « ${item.title} » ?`)) return;
    try {
      await decoupledDelete('menu_link_content', bundle, item.id);
      reload();
    } catch (err) {
      console.error('[MenuTree] Delete failed:', err.message);
    }
  }, [bundle, reload]);

  const ctxValue = useMemo(() => ({
    editing,
    onStartRename: (item) => setEditing({ key: item.key, mode: 'title' }),
    onStartLinkEdit: (item) => setEditing({ key: item.key, mode: 'link' }),
    onEditConfirm: handleEditConfirm,
    onEditCancel: () => setEditing(null),
    onSettings: (item) => openModal({
      isEdit: true,
      id: item.id,
      data: {
        title: item.title,
        link: { uri: normalizeUri(item.uri) },
        enabled: !!item.enabled,
        description: item.description || '',
      },
    }),
    onCreate: handleCreate,
    onDelete: handleDelete,
    onToggleSubtree: (item, collapsed) => setItems((cur) => collapseSubtree(cur, item.key, collapsed)),
  }), [editing, handleEditConfirm, handleCreate, handleDelete, openModal]);

  const createRoot = () => openModal({
    isEdit: false,
    data: { title: '', link: { uri: '' }, enabled: true, description: '', menu_name: menuName, parent: '', weight: 0 },
  });

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-sm font-semibold text-stone-800">
          Menu : <span className="font-mono text-stone-500">{menuName}</span>
          {saving && <Loader2 size={13} className="inline ml-2 animate-spin text-blue-500" />}
        </h1>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setItems((cur) => collapseAll(cur, false))}
            className="btn btn-ghost btn-sm flex items-center gap-1"
            title="Tout déplier"
          >
            <ChevronsUpDown size={13} /> Déplier
          </button>
          <button
            onClick={() => setItems((cur) => collapseAll(cur, true))}
            className="btn btn-ghost btn-sm flex items-center gap-1"
            title="Tout replier"
          >
            <ChevronsDownUp size={13} /> Replier
          </button>
          <button
            onClick={createRoot}
            className="btn btn-primary btn-sm flex items-center gap-1.5"
          >
            <Plus size={13} /> Ajouter un lien
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-stone-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : error ? (
        <div className="alert alert-danger items-center text-xs">
          <AlertTriangle size={14} /> {error}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-stone-400 py-8 text-center">Ce menu ne contient aucun lien éditable.</p>
      ) : (
        <div className="rounded-xl border border-stone-100 bg-white p-2">
          <MenuTreeCtx.Provider value={ctxValue}>
            <SortableTree
              items={items}
              onItemsChanged={handleItemsChanged}
              TreeItemComponent={MenuDndItemComponent}
              indentationWidth={20}
              pointerSensorOptions={{ activationConstraint: { distance: 5 } }}
            />
          </MenuTreeCtx.Provider>
        </div>
      )}
    </div>
  );
};

export default MenuAdminTree;
